import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useConfirmExtraction, useExtraction, useRejectExtraction } from "@/hooks/use-extractions";
import type { ExtractionView, ReviewedExtraction } from "@/schemas/extractions";
import type { Photo } from "@/schemas/photos";

const NOT_READ = "Not read";

const STATUS_LABELS = {
  suggested: "Suggested, needs confirming",
  confirmed: "Confirmed",
  rejected: "Rejected",
} as const;

const SUGGESTED = [
  { key: "equipmentType", label: "Equipment", suggested: "suggestedEquipmentType" },
  { key: "make", label: "Make", suggested: "suggestedMake" },
  { key: "model", label: "Model", suggested: "suggestedModel" },
  { key: "serial", label: "Serial", suggested: "suggestedSerial" },
] as const;

function isReviewed(view: ExtractionView): view is ReviewedExtraction {
  return "suggestedSerial" in view;
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm break-words">
        {value ?? <span className="text-muted-foreground">{NOT_READ}</span>}
      </dd>
    </div>
  );
}

const EDITABLE = [
  { key: "equipmentType", label: "Equipment", placeholder: "Leave empty if not shown" },
  { key: "make", label: "Make", placeholder: "Leave empty if not shown" },
  { key: "model", label: "Model", placeholder: "Leave empty if not shown" },
  { key: "serial", label: "Serial", placeholder: "Leave empty if the photo shows no serial" },
] as const;

type EditableKey = (typeof EDITABLE)[number]["key"];

/** A PM's three moves: confirm, correct what was read and confirm, or reject. */
function ReviewControls({ photo, view }: { photo: Photo; view: ReviewedExtraction }) {
  const confirm = useConfirmExtraction();
  const reject = useRejectExtraction();
  const [values, setValues] = useState<Record<EditableKey, string>>({
    equipmentType: view.equipmentType ?? "",
    make: view.make ?? "",
    model: view.model ?? "",
    serial: view.serial ?? "",
  });
  const busy = confirm.isPending || reject.isPending;
  // Only what the PM changed is sent: a field left out keeps its current value on the server.
  const edits = Object.fromEntries(
    EDITABLE.filter(({ key }) => values[key].trim() !== (view[key] ?? "")).map(({ key }) => [
      key,
      values[key].trim(),
    ]),
  );
  const changed = Object.keys(edits).length > 0;

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Correct anything the reader got wrong before confirming.
      </p>
      {EDITABLE.map(({ key, label, placeholder }) => (
        <div key={key} className="space-y-1">
          <Label htmlFor={`extraction-${key}`} className="text-xs text-muted-foreground">
            {label} to confirm
          </Label>
          <Input
            id={`extraction-${key}`}
            value={values[key]}
            onChange={(event) => setValues((prev) => ({ ...prev, [key]: event.target.value }))}
            // The viewer moves between photos on the arrow keys; here they move the caret.
            onKeyDown={(event) => event.stopPropagation()}
            placeholder={placeholder}
            className={key === "serial" ? "font-mono" : undefined}
          />
        </div>
      ))}
      <div className="flex gap-2">
        <Button
          size="sm"
          className="flex-1"
          data-testid="extraction-confirm"
          disabled={busy}
          onClick={() => confirm.mutate({ photoId: photo.id, request: edits })}
        >
          {view.status === "confirmed" ? "Save" : changed ? "Confirm with edit" : "Confirm"}
        </Button>
        {view.status !== "rejected" && (
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            data-testid="extraction-reject"
            disabled={busy}
            onClick={() => reject.mutate({ photoId: photo.id, request: {} })}
          >
            Reject
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * In the viewer: what was read off this photo's nameplate. The server decides what an account
 * gets, so an IE sees confirmed values or nothing at all, and a photo with no reading shows nothing.
 */
export function ExtractionPanel({ photo }: { photo: Photo }) {
  const { data: view, isPending, isError, refetch } = useExtraction(photo.id);

  if (isPending) return <Skeleton className="h-24 w-full" />;
  if (isError) {
    return (
      <Button variant="ghost" size="sm" onClick={() => refetch()}>
        Could not load the nameplate reading. Try again
      </Button>
    );
  }
  if (!view) return null;

  const reviewed = isReviewed(view) ? view : null;
  return (
    <section
      data-testid="extraction-panel"
      data-status={view.status}
      className="space-y-3 border-t pt-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-xs text-muted-foreground">Nameplate</h3>
        <Badge
          data-testid="extraction-status"
          variant={view.status === "confirmed" ? "default" : "outline"}
        >
          {STATUS_LABELS[view.status]}
        </Badge>
      </div>
      {view.status === "rejected" && (
        <p className="text-xs text-muted-foreground">
          A PM rejected this reading. It is not used anywhere.
        </p>
      )}
      <dl data-testid="extraction-fields" className="space-y-2">
        <Field label="Equipment" value={view.equipmentType} />
        <Field label="Make" value={view.make} />
        <Field label="Model" value={view.model} />
        <Field label="Serial" value={view.serial} />
        {reviewed && reviewed.status !== "confirmed" && (
          <div data-testid="extraction-confidence">
            <dt className="text-xs text-muted-foreground">Reader's confidence</dt>
            <dd className="mt-0.5 text-sm capitalize">{reviewed.confidence}</dd>
          </div>
        )}
        {reviewed?.status === "confirmed" &&
          SUGGESTED.map(({ key, label, suggested }) =>
            reviewed[suggested] !== reviewed[key] ? (
              <Field key={key} label={`${label} first suggested`} value={reviewed[suggested]} />
            ) : null,
          )}
      </dl>
      {reviewed && <ReviewControls key={view.status} photo={photo} view={reviewed} />}
    </section>
  );
}
